import { Injectable } from '@angular/core';
import { State, Action, StateContext, Store, Selector } from '@ngxs/store';
import { TrovataAppState } from 'src/app/core/models/state.model';
import { checkForBadHttpErrorMessage, checkToAddStateApiError, checkToRemoveStateApiError, StateApiError } from 'src/app/core/models/state-error.model';
import { combineLatest, Observable, throwError, firstValueFrom, take, filter } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { GetWorkflowsResponse, Workflow } from '../../../models/workflow.model';
import { WorkflowsService } from '../../../services/workflows/workflow.service';
import {
	ClearWorkflowsState,
	CreateWorkflow,
	DeleteWorkflow,
	GetWorkflowById,
	GetWorkflows,
	InitWorkflowsState,
	PostWorkflowAdminApproval,
	RemoveWorkflowFromStoreById,
	ResetWorkflowsState,
	UpdateWorkflow,
} from '../../actions/workflows.actions';
import { AdminApprovalService } from 'src/app/shared/services/admin-approval.service';
import {
	AdminApprovalRecordStatus,
	ChangeRequest,
	ChangeRequestType,
	PaymentsRecordType,
	PostAdminApprovalResponse,
} from 'src/app/shared/models/admin-approval.model';
import { PermissionMap, PermissionId } from 'src/app/features/settings/models/feature.model';
import { CustomerFeatureState } from 'src/app/features/settings/store/state/customer-feature.state';
import { AuthUser } from '@trovata/app/core/models/auth.model';
import { ReadyForReviewSnack, ReadyForReviewSnackParams, sortSnacksByDate } from 'src/app/shared/models/ready-for-review-snack.model';
import { DateTime } from 'luxon';
import { TrovataResourceType, TrovataResourceViewText } from 'src/app/shared/models/trovata.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export class WorkflowsStateModel {
	workflows: Workflow[];
	workflowSnacks: ReadyForReviewSnack[];
	apiErrors: StateApiError[];
	isCached: boolean;
	// Loading state
	workflowsLoading: boolean;
}

@State<WorkflowsStateModel>({
	name: 'workflows',
	defaults: {
		workflows: null,
		workflowSnacks: null,
		isCached: false,
		apiErrors: null,
		/**
		 * Loading state - Initialize to true to prevent flash of content on initial page load.
		 * Set to false by action handlers after data successfully loads.
		 */
		workflowsLoading: true,
	},
})
@Injectable()
export class WorkflowsState {
	private appReady$: Observable<boolean>;
	private authUser$: Observable<AuthUser>;
	private userAvailablePermissions$: Observable<PermissionMap>;
	private paymentsEnabled$: Observable<boolean>;
	private authUser: AuthUser;

	constructor(
		private workflowsService: WorkflowsService,
		private store: Store,
		private adminApprovalService: AdminApprovalService,
		private sanitizer: DomSanitizer
	) {
		this.appReady$ = this.store.select((state: TrovataAppState) => state.core.appReady);
		this.authUser$ = this.store.select((state: TrovataAppState) => state.authorization.authUser);
		this.userAvailablePermissions$ = this.store.select(CustomerFeatureState.permissionIds);
		this.paymentsEnabled$ = this.store.select(CustomerFeatureState.paymentsEnabled);
	}

	@Selector()
	static workflows(state: WorkflowsStateModel): Workflow[] {
		return state.workflows;
	}

	@Selector()
	static workflowsIsCached(state: WorkflowsStateModel): boolean {
		return state.isCached;
	}

	@Selector()
	static workflowsApiErrors(state: WorkflowsStateModel): StateApiError[] {
		return state.apiErrors;
	}

	@Selector()
	static workflowSnacks(state: WorkflowsStateModel): ReadyForReviewSnack[] {
		return state.workflowSnacks;
	}

	@Selector()
	static workflowsLoading(state: WorkflowsStateModel): boolean {
		return state.workflowsLoading;
	}

	@Action(InitWorkflowsState)
	async initWorkflowsState(context: StateContext<WorkflowsStateModel>): Promise<void> {
		const [appReady, permissions, authUser, paymentsEnabled]: [boolean, PermissionMap, AuthUser, boolean] = await firstValueFrom(
			combineLatest([this.appReady$, this.userAvailablePermissions$, this.authUser$, this.paymentsEnabled$]).pipe(
				filter(([ready, perms, user, enabled]: [boolean, PermissionMap, AuthUser, boolean]) => !!ready && !!perms && !!user && enabled !== undefined),
				take(1)
			)
		);

		if (!paymentsEnabled) {
			return;
		}

		this.authUser = authUser;

		if (permissions.has(PermissionId.readWorkflows)) {
			this.initDefaultWorkflowsState(context);
		} else {
			const cleared: Partial<WorkflowsStateModel> = { workflows: [] };
			context.patchState(cleared);
		}
	}

	@Action(GetWorkflows)
	getWorkflows(context: StateContext<WorkflowsStateModel>): Observable<GetWorkflowsResponse> {
		// Set loading state to true BEFORE API call
		context.patchState({ workflowsLoading: true });

		return this.workflowsService.getWorkflows().pipe(
			tap((getWorkflowsResponse: GetWorkflowsResponse) => {
				checkToRemoveStateApiError(context, GetWorkflows);
				const workflows: Workflow[] = getWorkflowsResponse.workflows;
				this.postProcessWorkflowsData(context, null, workflows);
			}),
			catchError(error => {
				// Set loading to false on error
				context.patchState({ workflowsLoading: false });
				return throwError(() => checkToAddStateApiError(error, context, GetWorkflows, 'Workflows are down right now. Please try again later.'));
			})
		);
	}

	@Action(CreateWorkflow)
	createWorkflow(context: StateContext<WorkflowsStateModel>, action: CreateWorkflow): Observable<Workflow> {
		return this.workflowsService.createWorkflow(action.workflowToCreate).pipe(
			tap((workflow: Workflow) => {
				const state: WorkflowsStateModel = context.getState();
				const workflowsCopy: Workflow[] = [...state.workflows];
				workflowsCopy.push(workflow);
				this.postProcessWorkflowsData(context, null, workflowsCopy);
			}),
			catchError(error => throwError(() => checkForBadHttpErrorMessage(error, 'Create workflow is down right now. Please try again later.')))
		);
	}

	@Action(UpdateWorkflow)
	updateWorkflow(context: StateContext<WorkflowsStateModel>, action: UpdateWorkflow): Observable<Workflow> {
		return this.workflowsService.updateWorkflow(action.workflowToUpdate).pipe(
			tap((workflow: Workflow) => {
				const state: WorkflowsStateModel = context.getState();
				const filteredWorkflows: Workflow[] = state.workflows.filter((filterWorkflow: Workflow) => filterWorkflow.workflowId !== workflow.workflowId);
				filteredWorkflows.push(workflow);
				this.postProcessWorkflowsData(context, null, filteredWorkflows);
			}),
			catchError(error => throwError(() => checkForBadHttpErrorMessage(error, 'Update workflow is down right now. Please try again later.')))
		);
	}

	@Action(DeleteWorkflow)
	deleteWorkflow(context: StateContext<WorkflowsStateModel>, action: DeleteWorkflow): Observable<Workflow> {
		return this.workflowsService.deleteWorkflow(action.workflowToDelete).pipe(
			tap((workflow: Workflow) => {
				const state: WorkflowsStateModel = context.getState();
				const filteredWorkflows: Workflow[] = state.workflows.filter(
					(filterWorkflow: Workflow) => filterWorkflow.workflowId !== action.workflowToDelete.workflowId
				);
				if (workflow && Object.keys(workflow).length) {
					filteredWorkflows.push(workflow);
				}
				this.postProcessWorkflowsData(context, null, filteredWorkflows);
			}),
			catchError(error => throwError(() => checkForBadHttpErrorMessage(error, 'Delete workflow is down right now. Please try again later.')))
		);
	}

	@Action(GetWorkflowById)
	getWorkflowById(context: StateContext<WorkflowsStateModel>, action: GetWorkflowById): Observable<GetWorkflowsResponse> {
		return this.workflowsService.getWorkflowById(action.workflowId).pipe(
			tap((getWorkflowsResponse: GetWorkflowsResponse) => {
				const state: WorkflowsStateModel = context.getState();
				const gotWorkflow: Workflow = getWorkflowsResponse.workflows.find((workflow: Workflow) => workflow.workflowId === action.workflowId);
				if (gotWorkflow) {
					const filteredWorkflows: Workflow[] = state.workflows.filter((workflow: Workflow) => workflow.workflowId !== gotWorkflow.workflowId);
					filteredWorkflows.push(gotWorkflow);
					this.postProcessWorkflowsData(context, null, filteredWorkflows);
				}
			}),
			catchError(error => throwError(() => checkForBadHttpErrorMessage(error, 'Workflow is down right now. Please try again later.')))
		);
	}

	@Action(PostWorkflowAdminApproval)
	postWorkflowAdminApproval(context: StateContext<WorkflowsStateModel>, action: PostWorkflowAdminApproval): Observable<PostAdminApprovalResponse> {
		return this.adminApprovalService.postAdminApproval(action.adminApproval, PaymentsRecordType.WORKFLOW).pipe(
			tap((response: PostAdminApprovalResponse) => {
				const state: WorkflowsStateModel = context.getState();
				const changeRequest: ChangeRequest = response.changeRequest;
				const approvalActionType: ChangeRequestType = changeRequest.changeType;
				const approvalStatus: AdminApprovalRecordStatus = changeRequest.status;
				const workflow: Workflow = response.record as Workflow;
				if (
					(approvalActionType === ChangeRequestType.CREATE && approvalStatus === AdminApprovalRecordStatus.APPROVED) ||
					(approvalActionType === ChangeRequestType.UPDATE && approvalStatus === AdminApprovalRecordStatus.APPROVED) ||
					(approvalActionType === ChangeRequestType.UPDATE && approvalStatus === AdminApprovalRecordStatus.REJECTED) ||
					(approvalActionType === ChangeRequestType.DELETE && approvalStatus === AdminApprovalRecordStatus.REJECTED)
				) {
					const filteredWorkflows: Workflow[] = state.workflows.filter((filterWorkflow: Workflow) => filterWorkflow.workflowId !== workflow.workflowId);
					filteredWorkflows.push(workflow);
					this.postProcessWorkflowsData(context, null, filteredWorkflows);
				} else if (
					(approvalActionType === ChangeRequestType.DELETE && approvalStatus === AdminApprovalRecordStatus.APPROVED) ||
					(approvalActionType === ChangeRequestType.CREATE && approvalStatus === AdminApprovalRecordStatus.REJECTED)
				) {
					const filteredWorkflows: Workflow[] = state.workflows.filter((filterWorkflow: Workflow) => filterWorkflow.workflowId !== changeRequest.recordId);
					this.postProcessWorkflowsData(context, null, filteredWorkflows);
				}
			}),
			catchError(error => throwError(() => checkForBadHttpErrorMessage(error, 'Workflow approval is down right now. Please try again later.')))
		);
	}

	@Action(ClearWorkflowsState)
	clearWorkflowsState(context: StateContext<WorkflowsStateModel>): void {
		const state: WorkflowsStateModel = context.getState();
		Object.keys(state).forEach((key: string) => {
			state[key] = null;
		});
		context.patchState(state);
	}

	@Action(ResetWorkflowsState)
	resetWorkflowsState(context: StateContext<WorkflowsStateModel>): void {
		context.dispatch(new ClearWorkflowsState());
		context.dispatch(new GetWorkflows());
	}

	private initDefaultWorkflowsState(context: StateContext<WorkflowsStateModel>): void {
		const state: WorkflowsStateModel = context.getState();
		state.workflows = [];
		state.workflowSnacks = [];
		state.apiErrors = [];
		state.isCached = false;
		context.patchState(state);
		context.dispatch(new GetWorkflows());
	}

	private postProcessWorkflowsData(context: StateContext<WorkflowsStateModel>, state?: WorkflowsStateModel, workflows?: Workflow[]): void {
		const stateToProcess: WorkflowsStateModel = state ? state : context.getState();
		const workflowsToProcess: Workflow[] = workflows ? workflows : stateToProcess.workflows;
		const workflowNotificationSnacks: ReadyForReviewSnack[] = this.getNotificationSnacks(workflowsToProcess);
		const sortedWorkflows: Workflow[] = this.sortWorkflowsAlphabetically(workflowsToProcess);
		stateToProcess.workflowSnacks = workflowNotificationSnacks;
		stateToProcess.workflows = sortedWorkflows;
		stateToProcess.isCached = true;
		// Set loading to false after workflows are processed
		stateToProcess.workflowsLoading = false;
		context.patchState(stateToProcess);
	}

	private getNotificationSnacks(workflows: Workflow[]): ReadyForReviewSnack[] {
		let workflowNotificationSnacks: ReadyForReviewSnack[] = [];
		workflows.forEach((workflow: Workflow) => {
			workflow.needsReview = this.checkIfWorkflowNeedsReview(workflow);
			if (workflow.needsReview) {
				const requestor: string = 'A user '; // TODO: Update with actual user name once apis are updated
				const actionText: string = `${workflow.changeRequest.changeType.toLowerCase()}d a `;
				const workflowNameText: string = `workflow (${workflow.name})`;
				const htmlString: string = `
        <span class="font-700-14-20">${requestor}</span>
        <span class="font-400-14-20">${actionText}</span>
        <span class="font-700-14-20">${workflowNameText}</span>
        `;
				const messageSafeHtml: SafeHtml = this.sanitizer.bypassSecurityTrustHtml(htmlString);
				const readyForReviewSnackParams: ReadyForReviewSnackParams = {
					date: DateTime.fromISO(workflow.changeRequest.createdAt).toLocaleString(DateTime.DATE_MED),
					messageHtml: messageSafeHtml,
					resource: workflow,
					resourceId: workflow.workflowId,
					resourceType: TrovataResourceType.workflow,
					resourceViewText: TrovataResourceViewText.Workflow,
					sortByDate: workflow.changeRequest.createdAt,
				};
				const snack: ReadyForReviewSnack = new ReadyForReviewSnack(readyForReviewSnackParams);
				workflowNotificationSnacks.push(snack);
			}
		});
		workflowNotificationSnacks = sortSnacksByDate(workflowNotificationSnacks);
		return workflowNotificationSnacks;
	}

	private checkIfWorkflowNeedsReview(workflow: Workflow): boolean {
		if (workflow.changeRequest && workflow.changeRequest.createdBy !== this.authUser['https://auth.trovata.io/userinfo/userId']) {
			return true;
		}
		return false;
	}

	private sortWorkflowsAlphabetically(workflows: Workflow[]): Workflow[] {
		const sortedWorkflows: Workflow[] = workflows.sort((workflowA: Workflow, workflowB: Workflow) => {
			if (workflowA.name.toLocaleLowerCase() < workflowB.name.toLocaleLowerCase()) {
				return -1;
			} else if (workflowA.name.toLocaleLowerCase() > workflowB.name.toLocaleLowerCase()) {
				return 1;
			}
			return 0;
		});
		return sortedWorkflows;
	}

	@Action(RemoveWorkflowFromStoreById)
	removeWorkflowFromStoreById(context: StateContext<WorkflowsStateModel>, action: RemoveWorkflowFromStoreById): void {
		const state: WorkflowsStateModel = context.getState();
		state.workflows = state.workflows.filter((filterWorkflow: Workflow) => filterWorkflow.workflowId !== action.workflowId);
		context.patchState(state);
	}
}
